/**
 * This page lists all triggers within a project.
 */

import {WHITE} from '@wandb/weave/common/css/globals.styles';
// @todo: Migrate to 'Tabs' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import * as Tabs from '@wandb/weave/components/Tabs';
import React, {useEffect, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {
  useFetchProjectTriggerExecutionsQuery,
  useFetchProjectTriggersQuery,
} from '../../../generated/graphql';
import {Analytics} from '../../../services/analytics';
import {ProjectData} from '../../../state/graphql/projectPageQuery';
import {propagateErrorsContext} from '../../../util/errors';
import {AutomationsIntercomChatButton} from '../../IntercomChatButton';
import {BaseTriggerDrawerInit} from '../../Launch/Triggers/CreateTriggerDrawer/EventStore';
import {EntryPoint} from '../../Launch/Triggers/CreateTriggerDrawer/types';
import {useIsProjectReadOnly} from '../../Registries/common/hooks';
import {AutomationsHistoryTab} from './AutomationsHistoryTab';
import {AutomationsTab} from './AutomationsTab';
import {TriggersHeader} from './TriggersHeader';
import * as S from './TriggersPage.styles';
import {ZeroTriggers} from './ZeroTriggers';

const AUTOMATIONS_TAB_NAME = 'automations';
const AUTOMATIONS_HISTORY_TAB_NAME = 'automations-history';

type TriggersPageProps = {
  entityName: string;
  project: ProjectData;
};

export const TriggersPage = ({entityName, project}: TriggersPageProps) => {
  const location = useLocation();
  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);
  const tabFromUrl = urlParams.get('tab');

  const [selectedTab, setSelectedTab] = useState(
    tabFromUrl === 'history'
      ? AUTOMATIONS_HISTORY_TAB_NAME
      : AUTOMATIONS_TAB_NAME
  );

  // Update selected tab when URL changes
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabFromUrl = urlParams.get('tab');
    switch (tabFromUrl) {
      case 'history':
        setSelectedTab(AUTOMATIONS_HISTORY_TAB_NAME);
        break;
      default:
        setSelectedTab(AUTOMATIONS_TAB_NAME);
    }
  }, [location.search]);

  const handleTabChange = (value: string) => {
    setSelectedTab(value);

    // Update URL to reflect tab change while preserving other params
    const urlParams = new URLSearchParams(location.search);
    if (value === AUTOMATIONS_HISTORY_TAB_NAME) {
      Analytics.track('Automation Execution History', {
        type: 'click_history_tab',
        location: location.pathname,
      });
    }
    switch (value) {
      case AUTOMATIONS_HISTORY_TAB_NAME:
        urlParams.set('tab', 'history');
        break;
      default:
        urlParams.delete('tab');
    }

    const newSearch = urlParams.toString();
    history.push({
      pathname: location.pathname,
      search: newSearch ? `?${newSearch}` : '',
    });
  };

  const projectTriggersQuery = useFetchProjectTriggersQuery({
    variables: {projectName: project.name, entityName, projectOnly: false},
    context: propagateErrorsContext(),
    // Turning off caching so that the automations list reloads when the user
    // switches tabs - this matters when the user creates an automation in the workspace,
    // then wants to see it in the automations list when they switch over.
    fetchPolicy: 'no-cache',
  });
  const {
    data: triggersData,
    loading: triggersLoading,
    refetch: refetchTriggers,
  } = projectTriggersQuery;

  const {data: executionsData, loading: executionsLoading} =
    useFetchProjectTriggerExecutionsQuery({
      variables: {projectName: project.name, entityName, projectOnly: false},
      context: propagateErrorsContext(),
      // Turning off caching for similar reasons as above.
      fetchPolicy: 'no-cache',
    });

  const baseTriggerDrawerInit: BaseTriggerDrawerInit = {
    entryPoint: EntryPoint.PROJECT,
    eventEntityName: entityName,
    eventProjectID: project.id,
    eventProjectName: project.name,
    artifactCollectionID: undefined,
    artifactCollectionName: undefined,
    existingAutomations: [],
    actionEntityName: entityName,
    suggestedMetricName: undefined,
    defaultEvent: undefined,
    defaultAction: undefined,
    panelLink: undefined,
  };

  const triggers = triggersData?.project?.triggers ?? [];
  const executions = executionsData?.project?.triggerExecutions?.edges ?? [];

  const style: React.CSSProperties = {};
  if (triggers.length === 0 && executions.length === 0) {
    style.backgroundColor = WHITE;
    style.height = '100%';
  }
  const {isProjectReadOnly: readOnly} = useIsProjectReadOnly(
    entityName,
    project.name
  );

  return (
    <S.Content style={style}>
      <TriggersHeader
        entityName={entityName}
        projectID={project.id}
        projectName={project.name}
        refetchTriggers={refetchTriggers}
      />
      {triggers.length === 0 &&
      !triggersLoading &&
      executions.length === 0 &&
      !executionsLoading ? (
        <ZeroTriggers />
      ) : (
        <Tabs.Root value={selectedTab} onValueChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Trigger value={AUTOMATIONS_TAB_NAME}>
              All automations
            </Tabs.Trigger>
            <Tabs.Trigger value={AUTOMATIONS_HISTORY_TAB_NAME}>
              History
            </Tabs.Trigger>
          </Tabs.List>
          <div className="pt-16">
            <Tabs.Content value={AUTOMATIONS_TAB_NAME}>
              <AutomationsTab
                entityName={entityName}
                projectName={project.name}
                baseTriggerDrawerInit={baseTriggerDrawerInit}
                readOnly={readOnly}
                projectTriggersQuery={projectTriggersQuery}
              />
            </Tabs.Content>
            <Tabs.Content value={AUTOMATIONS_HISTORY_TAB_NAME}>
              <AutomationsHistoryTab
                entityName={entityName}
                projectName={project.name}
              />
            </Tabs.Content>
          </div>
        </Tabs.Root>
      )}
      <AutomationsIntercomChatButton />
    </S.Content>
  );
};
